import { Controller } from "@hotwired/stimulus"

/**
 * Manages skill proficiency editing for agent profiles.
 * Renders skill rows with range sliders (0-100%), add/remove actions,
 * and generates hidden inputs for form submission.
 *
 * Rows are sorted alphabetically by skill name and stay put while dragging:
 * `updateProficiency` mutates the slider fill in place instead of re-rendering
 * the whole list (a full re-render would destroy the slider mid-drag).
 *
 * Usage:
 *   <div data-controller="support--skill-editor"
 *        data-support--skill-editor-skills-value='{"billing":0.9}'
 *        data-support--skill-editor-field-name-value="support_agent_profile[skills]">
 */
export default class extends Controller {
  static values = {
    skills: { type: Object, default: {} },
    fieldName: { type: String, default: "support_agent_profile[skills]" },
    emptyText: { type: String, default: "No skills assigned" },
  }

  static targets = ["list", "hiddenInputs", "newSkillInput"]

  connect() {
    // Internal mutable source of truth, seeded from the Stimulus value.
    this.skills = { ...this.skillsValue }
    this.render()
  }

  addSkill(event) {
    event.preventDefault()
    const input = this.newSkillInputTarget
    const key = input.value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_")
    if (!key || key.length === 0) return
    if (this.skills[key] !== undefined) return

    this.skills[key] = 1.0
    input.value = ""
    input.focus()
    this.render()
  }

  removeSkill(event) {
    event.preventDefault()
    const key = event.currentTarget.dataset.skillKey
    if (!key) return

    delete this.skills[key]
    this.render()
  }

  updateProficiency(event) {
    const slider = event.currentTarget
    const key = slider.dataset.skillKey
    const value = parseFloat(slider.value) / 100.0
    if (!key || isNaN(value)) return

    this.skills[key] = Math.round(value * 100) / 100
    const pct = Math.round(this.skills[key] * 100)

    // Update fill + colours in place — do NOT re-render the list, or the
    // slider being dragged would be destroyed and the drag interrupted.
    slider.style.setProperty("--skill-fill", pct + "%")
    slider.style.setProperty("--skill-fill-color", this.fillColor(pct))

    const pctLabel = slider.parentElement.querySelector("[data-skill-pct]")
    if (pctLabel) {
      pctLabel.textContent = pct + "%"
      pctLabel.className = this.pctLabelClass(pct)
    }

    this.renderHiddenInputs()
  }

  render() {
    this.renderSkillList()
    this.renderHiddenInputs()
  }

  renderSkillList() {
    const list = this.listTarget
    while (list.firstChild) list.removeChild(list.firstChild)

    const entries = Object.entries(this.skills).sort((a, b) =>
      a[0].localeCompare(b[0]),
    )

    if (entries.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-sm text-muted-foreground py-2"
      empty.textContent = this.emptyTextValue
      list.appendChild(empty)
      return
    }

    entries.forEach(([key, proficiency]) => {
      const pct = Math.round(proficiency * 100)
      const row = document.createElement("div")
      row.className =
        "flex items-center gap-3 py-2 border-b border-border last:border-0"

      const label = document.createElement("span")
      label.className = "text-sm font-medium text-foreground w-32 truncate"
      label.textContent = key

      const sliderWrap = document.createElement("div")
      sliderWrap.className = "flex-1 flex items-center gap-3"

      const slider = document.createElement("input")
      slider.type = "range"
      slider.min = "0"
      slider.max = "100"
      slider.value = String(pct)
      slider.dataset.skillKey = key
      slider.dataset.action = "input->support--skill-editor#updateProficiency"
      slider.className = "skill-slider flex-1"
      slider.style.setProperty("--skill-fill", pct + "%")
      slider.style.setProperty("--skill-fill-color", this.fillColor(pct))

      const pctLabel = document.createElement("span")
      pctLabel.dataset.skillPct = key
      pctLabel.className = this.pctLabelClass(pct)
      pctLabel.textContent = pct + "%"

      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "text-danger p-1"
      removeBtn.dataset.skillKey = key
      removeBtn.dataset.action = "click->support--skill-editor#removeSkill"
      removeBtn.setAttribute("aria-label", "Remove " + key)

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      svg.setAttribute("class", "w-4 h-4")
      svg.setAttribute("fill", "none")
      svg.setAttribute("viewBox", "0 0 24 24")
      svg.setAttribute("stroke", "currentColor")
      svg.setAttribute("stroke-width", "2")
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      )
      path.setAttribute("stroke-linecap", "round")
      path.setAttribute("stroke-linejoin", "round")
      path.setAttribute("d", "M6 18L18 6M6 6l12 12")
      svg.appendChild(path)
      removeBtn.appendChild(svg)

      sliderWrap.appendChild(slider)
      sliderWrap.appendChild(pctLabel)

      row.appendChild(label)
      row.appendChild(sliderWrap)
      row.appendChild(removeBtn)
      list.appendChild(row)
    })
  }

  renderHiddenInputs() {
    const container = this.hiddenInputsTarget
    while (container.firstChild) container.removeChild(container.firstChild)

    Object.entries(this.skills).forEach(([key, value]) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.fieldNameValue + "[" + key + "]"
      input.value = String(value)
      container.appendChild(input)
    })
  }

  fillColor(pct) {
    if (pct > 70) return "var(--color-success, #22c55e)"
    if (pct >= 30) return "var(--color-warning, #f59e0b)"
    return "var(--color-danger, #ef4444)"
  }

  pctLabelClass(pct) {
    const color =
      pct > 70 ? "text-success" : pct >= 30 ? "text-warning" : "text-danger"
    return "text-xs font-medium w-10 text-right " + color
  }
}
